import type { ApiErrorBody } from './types';

/**
 * The one place this application talks to the API.
 *
 * Two responsibilities, and deliberately no others: attach the caller's
 * identity (dev-mode assertion or a real, cookie-based session, mutually
 * exclusive — see below), and turn any failure into an `ApiError` the UI
 * can present without leaking internals to the user.
 *
 * ---------------------------------------------------------------------
 * TWO IDENTITY MODES, NEVER BOTH AT ONCE
 *
 * **Development identity** — `Authorization: Bearer dev:<employee-uuid>`,
 * accepted only when the API is started with `AUTH_MODE=dev` (docs/API.md
 * §4.3). That is an identity assertion, not a credential — there is no
 * secret here to protect. Read from `VITE_DEV_EMPLOYEE_ID`; if it is unset,
 * local mode applies instead.
 *
 * **Local (EmployeeProfile-owned) identity** — a real Better Auth session,
 * established via `../auth/localAuth.ts`'s password + TOTP flow, carried
 * as an HttpOnly cookie the browser attaches automatically to every
 * same-origin request. Unlike the Entra flow this replaced
 * (`docs/AUTHENTICATION_REASSESSMENT.md`), this needs no build-time
 * configuration at all — no client ID, no tenant — so it is simply
 * whichever mode applies when dev mode is not configured, not a third,
 * separately-gated state. There is no more "unconfigured" state.
 *
 * `resolveIdentityMode()` below decides which applies, once, from
 * configuration — never guessed from `NODE_ENV` or any other signal.
 * Dev-mode's own code path is untouched by this change: every existing
 * test that calls `configureApiClient({ devEmployeeId })` exercises
 * exactly the same branch it always has.
 * ---------------------------------------------------------------------
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ClientConfig {
  baseUrl: string;
  devEmployeeId: string | undefined;
}

type IdentityMode = { kind: 'dev'; employeeId: string } | { kind: 'local' };

function readConfig(): ClientConfig {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
  return {
    baseUrl: env.VITE_API_BASE_URL ?? '/api',
    devEmployeeId: env.VITE_DEV_EMPLOYEE_ID,
  };
}

/** Overridable so tests can drive the client without touching import.meta. */
let config: ClientConfig = readConfig();

export function configureApiClient(next: Partial<ClientConfig>): void {
  config = { ...config, ...next };
}

export function getDevEmployeeId(): string | undefined {
  return config.devEmployeeId;
}

/** Which identity mode is active, decided once from configuration.
 * Exported so `App.tsx` can gate rendering on it without duplicating this
 * decision. */
export function resolveIdentityMode(): IdentityMode {
  if (config.devEmployeeId) return { kind: 'dev', employeeId: config.devEmployeeId };
  return { kind: 'local' };
}

/**
 * A user-facing sentence for any failure. Deliberately never the API's own
 * `message`, which is written for developers and can name ids the reader
 * has no business seeing — and never a stack trace (docs/API.md §7 keeps
 * internals server-side; the UI must not undo that).
 */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.status) {
      case 401:
        return 'Your session is not recognised. Please sign in again.';
      case 403:
        return 'You do not have permission to view this information.';
      case 404:
        return 'That record could not be found, or is outside what you are able to view.';
      case 400:
        return 'That request was not valid.';
      case 409:
      case 422:
        // The two statuses where the API's own message is the useful one.
        //
        // A 422 explains a rule the reader can act on — "self is not
        // permitted to assess Diligence" — and a 409 explains a conflict
        // with the record's current state — "this has already been
        // corrected; correct the record that replaced it instead". Both
        // name an attribute or an action rather than an internal id, and a
        // generic sentence would strip exactly the part that tells someone
        // what to do next. Every other status keeps its translation
        // (docs/API.md §7).
        return error.message;
      default:
        return 'Something went wrong. Please try again.';
    }
  }
  return 'Could not reach the Employee Profile service. Check that the API is running and try again.';
}

/** The one branch point between the two identity modes — everything below
 * it (error translation, JSON parsing) is identical either way. Dev mode
 * still attaches an explicit `Authorization` header (an identity
 * assertion, never a credential — see this file's own header comment);
 * local mode attaches nothing here at all — the browser sends Better
 * Auth's own HttpOnly session cookie automatically on every same-origin
 * request, exactly like every other same-origin cookie, which is why
 * `request()` sets `credentials: 'same-origin'` explicitly below rather
 * than leaving it to fetch's own default. */
function resolveAuthorizationHeader(): string | undefined {
  const mode = resolveIdentityMode();
  switch (mode.kind) {
    case 'dev':
      return `Bearer dev:${mode.employeeId}`;
    case 'local':
      return undefined;
  }
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const { baseUrl } = config;
  const authorization = resolveAuthorizationHeader();

  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(authorization ? { Authorization: authorization } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (!response.ok) {
    let code = 'request_failed';
    let message = response.statusText;
    try {
      const body = (await response.json()) as ApiErrorBody;
      if (body?.error) {
        code = body.error.code;
        message = body.error.message;
      }
    } catch {
      // A non-JSON error body (a proxy failure, say) is not itself an error
      // worth surfacing — the status is what the UI reacts to.
    }
    throw new ApiError(response.status, code, message);
  }

  return (await response.json()) as T;
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { signal });
}

/**
 * The one write verb this client has (Mission 014).
 *
 * It shares `request` with `apiGet` deliberately: the identity header, the
 * error envelope and the user-facing translation must behave identically
 * whichever direction the data is going. A separate write path with its own
 * error handling is how a mutation ends up showing a reader a raw API
 * message that no GET would ever have shown them.
 *
 * There is no `apiPut`, `apiPatch` or `apiDelete`, because there is nothing
 * to update or delete: assessments are append-only
 * (docs/ASSESSMENT_FRAMEWORK.md §1), and the API exposes no such routes.
 */
export async function apiPost<TResponse, TBody = unknown>(path: string, body: TBody): Promise<TResponse> {
  return request<TResponse>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * A partial update (Mission 018).
 *
 * The only verb in this client that mutates an existing row, and it exists
 * for the only rows in the product that are updated in place: organisation
 * definitions. `docs/API.md` §6 explains why assessments never take one —
 * a correction is a new record, not an edit — and why definitions do.
 *
 * An absent field means "leave it alone", which is exactly the distinction
 * PUT cannot express, and why there is no `apiPut` here.
 */
export async function apiPatch<TResponse, TBody = unknown>(path: string, body: TBody): Promise<TResponse> {
  return request<TResponse>(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * A file upload (Mission 168, Employee Photo/Document — the only binary
 * content this application ever sends). Deliberately no explicit
 * `Content-Type` header: the browser sets `multipart/form-data` with its
 * own correctly-generated boundary when the body is a real `FormData`
 * instance, and overriding that manually is a well-known way to send a
 * boundary the browser itself does not honour.
 */
export async function apiUpload<TResponse>(path: string, file: File, fields: Record<string, string | null | undefined> = {}): Promise<TResponse> {
  const formData = new FormData();
  formData.append('file', file);
  for (const [key, value] of Object.entries(fields)) {
    if (value !== null && value !== undefined) formData.append(key, value);
  }
  return request<TResponse>(path, { method: 'POST', body: formData });
}
